'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiFillCheckCircle } from 'react-icons/ai';
import { MdCancel } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useUpdateContract, useValidateContract } from '@/hooks/queries/useDataContracts';
import { useSecretsQuery } from '@/hooks/queries/useSecrets';
import { SecretListItem, SecretTypeEnum } from '@/types/secret';
import { dataPlatformSecretsTabPath } from '@/utils/secretsLink';
import { setContractSecretName } from '@/utils/yamlSecretEdit';

import styles from './EditCredentialModal.module.scss';

type Props = {
  contractId: string;
  contractName: string;
  currentYaml: string;
  currentSecretName: string | null;
  onClose: () => void;
};

type Result = 'pending' | 'success' | 'error';

const editCredentialSchema = z.object( {
  secretName: z.string().min( 1, 'Pick a data platform secret' )
} );

type EditCredentialFormData = z.infer<typeof editCredentialSchema>;

const buildContractFile = ( yaml: string ): File =>
  new File( [ yaml ], 'contract.yaml', { type: 'application/x-yaml' } );

export default function EditCredentialModal( {
  contractId,
  contractName,
  currentYaml,
  currentSecretName,
  onClose
}: Props ) {
  const { data: secrets, isLoading: isLoadingSecrets } = useSecretsQuery();
  const validateMutation = useValidateContract();
  const updateMutation = useUpdateContract();

  const [ result, setResult ] = useState<Result>( 'pending' );
  const [ errorMessage, setErrorMessage ] = useState( '' );

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty, isValid }
  } = useForm<EditCredentialFormData>( {
    resolver: zodResolver( editCredentialSchema ),
    mode: 'onChange',
    defaultValues: { secretName: currentSecretName ?? '' }
  } );
  const secretName = watch( 'secretName' );

  const dataPlatformSecrets = useMemo<SecretListItem[]>( () => {
    return ( secrets ?? [] )
      .filter( s => s.type === SecretTypeEnum.DATA_PLATFORM )
      .sort( ( a, b ) => a.name.localeCompare( b.name ) );
  }, [ secrets ] );

  const isSubmitting = validateMutation.isPending || updateMutation.isPending;

  const onSubmit = handleSubmit( async ( data ) => {
    let newYaml: string;
    try {
      newYaml = setContractSecretName( currentYaml, data.secretName );
    } catch ( err ) {
      const message = err instanceof Error ? err.message : 'Failed to update contract YAML.';
      setResult( 'error' );
      setErrorMessage( message );
      return;
    }

    const file = buildContractFile( newYaml );

    try {
      const validation = await validateMutation.mutateAsync( file );
      if ( !validation.is_valid ) {
        setResult( 'error' );
        setErrorMessage( validation.message || 'Contract validation failed.' );
        return;
      }
    } catch ( err ) {
      const message = err instanceof Error ? err.message : 'Validation failed.';
      setResult( 'error' );
      setErrorMessage( message );
      return;
    }

    try {
      await updateMutation.mutateAsync( { contractId, file } );
      setResult( 'success' );
      toast.success( 'Credential updated' );
    } catch ( err ) {
      const message = err instanceof Error ? err.message : 'Update failed.';
      setResult( 'error' );
      setErrorMessage( message );
    }
  } );

  const retry = useCallback( () => {
    setResult( 'pending' );
    setErrorMessage( '' );
  }, [] );

  return match( result )
    .with( 'success', () => (
      <Modal className={ styles.modal } modalType="edit-credential" initialSize="small">
        <div className={ styles.inner }>
          <div className={ styles.header }>
            <Heading as="h2" size="md">Edit credential</Heading>
            <p className={ styles.subtitle }>{ contractName }</p>
          </div>
          <div className={ styles.resultOverlay }>
            <AiFillCheckCircle className={ clsx( styles.resultIcon, styles.resultIconSuccess ) } />
            <p className={ styles.resultMessage }>Credential updated</p>
            <p className={ styles.resultSubMessage }>
              The contract is reprocessing with the new credential.
            </p>
          </div>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" onClick={ onClose } data-testid="edit-credential-close">
              Close
            </Button>
          </div>
        </div>
      </Modal>
    ) )
    .with( 'error', () => (
      <Modal className={ styles.modal } modalType="edit-credential" initialSize="small">
        <div className={ styles.inner }>
          <div className={ styles.header }>
            <Heading as="h2" size="md">Edit credential</Heading>
            <p className={ styles.subtitle }>{ contractName }</p>
          </div>
          <div className={ styles.resultOverlay }>
            <MdCancel className={ clsx( styles.resultIcon, styles.resultIconError ) } />
            <p className={ styles.resultMessage }>Update failed</p>
            <p className={ styles.resultSubMessage } data-testid="edit-credential-error-message">
              { errorMessage || 'Could not update the contract credential.' }
            </p>
          </div>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" onClick={ onClose } data-testid="edit-credential-close">
              Close
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={ retry }
              data-testid="edit-credential-retry"
            >
              Try again
            </Button>
          </div>
        </div>
      </Modal>
    ) )
    .with( 'pending', () => (
      <Modal className={ styles.modal } modalType="edit-credential" initialSize="small">
        <form className={ styles.inner } onSubmit={ onSubmit }>
          <div className={ styles.header }>
            <Heading as="h2" size="md">Edit credential</Heading>
            <p className={ styles.subtitle }>{ contractName }</p>
          </div>

          <div className={ styles.content }>
            <Text as="p" size="sm" className={ styles.helper }>
              Pick a stored data platform secret. Saving rewrites the contract&apos;s
              YAML <code>servers.source.secrets.secret_name</code> and re-uploads it.
            </Text>

            { isLoadingSecrets ? (
              <Loader />
            ) : dataPlatformSecrets.length === 0 ? (
              <p className={ styles.empty } data-testid="edit-credential-no-secrets">
                No data platform secrets exist yet. Create one in{ ' ' }
                <Link to={ dataPlatformSecretsTabPath() }>Settings</Link>{ ' ' }
                first.
              </p>
            ) : (
              <Menu
                variant="select"
                searchable
                label="Data platform secret"
                value={ secretName || 'Select a secret…' }
                data-testid="edit-credential-secret-select"
              >
                { dataPlatformSecrets.map( s => (
                  <MenuItem
                    key={ s.id }
                    value={ s.name }
                    data-testid={ `edit-credential-secret-option-${ s.name }` }
                    onClick={ () => setValue( 'secretName', s.name, { shouldDirty: true, shouldValidate: true } ) }
                  />
                ) ) }
              </Menu>
            ) }
          </div>

          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" onClick={ onClose } data-testid="edit-credential-cancel">
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={ !isValid || !isDirty || dataPlatformSecrets.length === 0 }
              loading={ isSubmitting }
              data-testid="edit-credential-save"
            >
              Save
            </Button>
          </div>
        </form>
      </Modal>
    ) )
    .exhaustive();
}
